import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {CheckboxInputGrid} from 'Quoting/components/shared/CheckboxInputGrid';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormControlElement} from 'Quoting/components/shared/QuoteFormControlElement';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {NoOp} from 'Shared/utilities/IOUtilities';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getBenefitLabelMappingforGAI7} from '../../PlanTemplates/ACC/GAI7Utilities';
import {
  getOptionFromValue,
  getOptionFromValueTimePeriodHSS,
  unitsOptions,
} from '../../PlanTemplates/ACC/benefitDefaults';
import {getPlanPropertiesPath} from '../../PlansPropertyPaths';
import {getParentStructurePath} from '../Common/PropertyPaths';
import {formatCurrencyValue} from './ACCBenefits';
import {
  getHealthScreeningServices,
  getOrganizedSportsActivity,
  getOutPatientPhysicianTreatment,
} from './PropertyPaths/propertyPathACC';

const Portability = () => {
  const rows: GridRowProps[] = [];
  return <FormRowGrid rows={rows} />;
};

const FixedBenefitCategoriesBlock = () => {
  const {getQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getHealthScreeningServices(selectedPlan?.planIndex);

  const benefit = 'healthScreeningServicesRiderDetails';
  const path = getBenefitLabelMapping(`${benefit}`, `${getParentStructurePath(0, 0)}.${benefit}`, {
    type: 'options',
    flag: 'isOptionApplied',
  });

  const parentPath = `benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`;
  const planProperties = getPlanPropertiesPath(selectedPlan?.planIndex ?? 0);
  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');
  const benefitPaths = getBenefitLabelMappingforGAI7(
    `${getQuoteFieldValue<string>(planProperties.planCd)}exclusions`,
    parentPath,
    {
      type: 'exclusions',
      flag: 'isExclusion',
    },
    situsStateCd
  );

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'fixed-benefit-basic-row-1'},
      items: [
        {
          key: propertyPaths.healthScreeningServicesRiderDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.healthScreeningServicesRiderDetails.benefitAmount)
              )}
              disabled
              id={propertyPaths.healthScreeningServicesRiderDetails.benefitAmount}
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.healthScreeningServicesRiderDetails.isPreventativeEnabled,
          element: (
            <QuoteFormElement
              elementType="FormControl"
              controlType="switch"
              label="Preventive"
              checked={getQuoteFieldValue<boolean>(
                propertyPaths.healthScreeningServicesRiderDetails.isPreventativeEnabled
              )}
              id={propertyPaths.healthScreeningServicesRiderDetails.isPreventativeEnabled}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <>
      <FormRowGrid rows={rows} />
      <FormWrapper title="Basic" withSeparator>
        {path && <CheckboxInputGrid state={path} gridLength={3} />}
      </FormWrapper>
      <FormWrapper title="Exclusions" withSeparator>
        {Array.isArray(benefitPaths) && benefitPaths.length > 0 ? (
          <Grid container>
            {benefitPaths.map(
              ({label, path}) =>
                label !== 'On the job accident' && (
                  <Grid item key={label} xs={12} sm={6} md={3} style={{padding: '1rem'}}>
                    <QuoteFormControlElement
                      controlType="checkbox"
                      id={path}
                      label={label}
                      checked={getQuoteFieldValue<boolean>(path)}
                      disabled
                      key={path}
                    />
                  </Grid>
                )
            )}
          </Grid>
        ) : null}
      </FormWrapper>
    </>
  );
};

export const HealthScreeningServices = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getHealthScreeningServices(selectedPlan?.planIndex);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'health-screening-services-row-1'},
      items: [
        {
          key: 'health-screening-services-type-of-benefit',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Type of Benefit"
              options={getOptionFromValue(
                getQuoteFieldValue<string>(propertyPaths.healthScreeningServicesRiderDetails.healthScreeningType) ?? ''
              )}
              value={getQuoteFieldValue<string>(propertyPaths.healthScreeningServicesRiderDetails.healthScreeningType)}
              id={propertyPaths.healthScreeningServicesRiderDetails.healthScreeningType}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.healthScreeningServicesRiderDetails.healthScreeningHashUnits,
          element: (
            <QuoteFormElement
              elementType="Select"
              type="number"
              label="Units"
              value={getQuoteFieldValue<string>(
                propertyPaths.healthScreeningServicesRiderDetails.healthScreeningHashUnits
              )}
              options={unitsOptions}
              id={propertyPaths.healthScreeningServicesRiderDetails.healthScreeningHashUnits}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'health-screening-services-row-2'},
      items: [
        {
          key: propertyPaths.healthScreeningServicesRiderDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={getOptionFromValueTimePeriodHSS(
                getQuoteFieldValue<string>(propertyPaths.healthScreeningServicesRiderDetails.timePeriodCd) ?? ''
              )}
              value={getQuoteFieldValue<string>(propertyPaths.healthScreeningServicesRiderDetails.timePeriodCd)}
              id={propertyPaths.healthScreeningServicesRiderDetails.timePeriodCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {
        key: 'health-screening-row-3',
      },
      items: [
        {
          key: `health-screening-fixed-options-${selectedPlan?.planIndex}`,
          element: <FixedBenefitCategoriesBlock />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const OrganizedSportsActivity = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const {getBenefitLabelMapping} = useQuoteField();

  const benefit = 'organizedSportsActivityInjuryRiderDetails';
  const paths = getBenefitLabelMapping(`${benefit}`, `${getParentStructurePath(0, 0)}.${benefit}`);

  const propertyPaths = getOrganizedSportsActivity(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'organized-sports-activity-row-1'},
      items: [
        {
          key: propertyPaths.organizedSportsActivityInjuryRiderDetails.benefitPct,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="% of Plan Benefit Amount (Recommended 25%)"
              searchKey={`ACCOrganizedSportsActivityInjuryRider.benefitPct`}
              value={getQuoteFieldValue<number>(propertyPaths.organizedSportsActivityInjuryRiderDetails.benefitPct)}
              disabled
              required
              id={propertyPaths.organizedSportsActivityInjuryRiderDetails.benefitPct}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'organized-sports-activity-row-2'},
      items: [
        {
          key: 'organized-sports-activity-exclusions',
          element: (
            <Grid container>
              {paths.map(({label, path}) => (
                <Grid item key={label} xs={4} sm={4} md={4} style={{padding: '1rem'}}>
                  <QuoteFormControlElement
                    controlType="checkbox"
                    id={path}
                    label={label}
                    checked={getQuoteFieldValue<boolean>(path)}
                    disabled
                  />
                </Grid>
              ))}
            </Grid>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AutomobileAccidentRider = () => {
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'automobile-accident-rider-row-1'},
      items: [
        {
          key: 'automobile-accident-rider-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="% of Plan Benefit Amount (Recommended 10%)"
              options={[]}
              value="10%"
              id="automobileAccidentRiderBenefitAmount"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'automobile-accident-rider-row-2'},
      items: [
        {
          key: 'automobile-accident-rider-facility-care',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Facility Care"
              checked={true}
              id="automobileAccidentRiderFacilityCare"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'automobile-accident-rider-injuries',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Injuries"
              checked={true}
              id="automobileAccidentRiderInjuries"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'automobile-accident-rider-treatement-urgent-care',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Treatment and Urgent Care"
              checked={true}
              id="automobileAccidentRiderTreatementUrgentCare"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
      ],
    },
    {
      rowProps: {key: 'automobile-accident-rider-row-3'},
      items: [
        {
          key: 'automobile-accident-rider-treatement-support-recovery',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Treatment, Support, and Recovery"
              checked={true}
              id="automobileAccidentRiderTreatementSupportRecovery"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'automobile-accident-rider-dislocation-fracture',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Dislocation and Fracture Rider"
              checked={true}
              id="automobileAccidentRiderDislocationFracture"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const OrganizedSportsActivityInjuryRider = () => {
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'organized-sports-activity-injury-row-1'},
      items: [
        {
          key: 'organized-sports-activity-injury-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="% of Plan Benefit Amount (Recommended 10%)"
              options={[]}
              value="10%"
              id="organizedSportsActivityInjuryBenefitAmount"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'organized-sports-activity-injury-row-2'},
      items: [
        {
          key: 'organized-sports-activity-injury-facility-care',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Facility Care"
              checked={true}
              id="organizedSportsActivityInjuryFacilityCare"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'organized-sports-activity-injury-injuries',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Injuries"
              checked={true}
              id="organizedSportsActivityInjuryInjuries"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'organized-sports-activity-injury-treatement-urgent-care',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Treatment and Urgent Care"
              checked={true}
              id="organizedSportsActivityInjuryTreatementUrgentCare"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
      ],
    },
    {
      rowProps: {key: 'organized-sports-activity-injury-row-3'},
      items: [
        {
          key: 'organized-sports-activity-injury-treatement-support-recovery',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Treatment, Support, and Recovery"
              checked={true}
              id="organizedSportsActivityInjuryTreatementSupportRecovery"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'organized-sports-activity-injury-dislocation-fracture',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Dislocation and Fracture Rider"
              checked={true}
              id="organizedSportsActivityInjuryDislocationFracture"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const GroupPolicyholderRider = () => {
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'group-policyholder-row-1'},
      items: [
        {
          key: 'group-policyholder-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="% of Plan Benefit Amount (Recommended 10%)"
              options={[]}
              value="10%"
              id="groupPolicyholderBenefitAmount"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'group-policyholder-row-2'},
      items: [
        {
          key: 'group-policyholder-facility-care',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Facility Care"
              checked={true}
              id="groupPolicyholderFacilityCare"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'group-policyholder-injuries',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Injuries"
              checked={true}
              id="groupPolicyholderInjuries"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'group-policyholder-treatement-urgent-care',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Accident Treatment and Urgent Care"
              checked={true}
              id="groupPolicyholderTreatementUrgentCare"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
      ],
    },
    {
      rowProps: {key: 'group-policyholder-row-3'},
      items: [
        {
          key: 'group-policyholder-treatement-support-recovery',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Treatment, Support, and Recovery"
              checked={true}
              id="groupPolicyholderTreatementSupportRecovery"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
        {
          key: 'group-policyholder-dislocation-fracture',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Dislocation and Fracture Rider"
              checked={true}
              id="groupPolicyholderDislocationFracture"
              onChange={e => NoOp}
            />
          ),
          props: {xs: 4, sm: 4, md: 4},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const OutpatientPhysicianTreatements = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getOutPatientPhysicianTreatment(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'out-patient-physician-treatement-row-1'},
      items: [
        {
          key: propertyPaths.outpatientPhysicianTreatmentsDetails.typeOfRider,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Type of Rider"
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.typeOfRider)}
              required
              disabled
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.typeOfRider}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.outpatientPhysicianTreatmentsDetails.units,
          element: (
            <QuoteFormElement
              elementType="Select"
              type="number"
              label="Units"
              options={unitsOptions}
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.units)}
              required
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.units}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'out-patient-physician-treatement-row-2'},
      items: [
        {
          key: propertyPaths.outpatientPhysicianTreatmentsDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Maximum Number of Days (Per Covered Person)"
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.outpatientPhysicianTreatmentsDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCOutpatientPhysicianTreatments.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'out-patient-physician-treatement-row-2'},
      items: [
        {
          key: propertyPaths.outpatientPhysicianTreatmentsDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Daily Benefit Amount (Per Unit)"
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.benefitAmount)}
              disabled
              required
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.benefitAmount}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AdditionalOptionsandBenefitsGAI7Block = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getOutPatientPhysicianTreatment(selectedPlan?.planIndex);

  const organizedSportsActivityPaths = getOrganizedSportsActivity(selectedPlan?.planIndex);

  const additionalOptNBenefits: ConditionalSection[] = [
    {
      Component: Portability,
      key: 'Portability',
      disabled: true,
      label: 'PORTABILITY (UNLIMITED)',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isPortabilityApplied),
    },
    {
      Component: HealthScreeningServices,
      key: 'HealthScreeningServices',
      label: 'HEALTH SCREENING SERVICES',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isHealthScreeningServicesRiderApplied),
      disabled: true,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isHealthScreeningServicesRiderApplied, isApplied);
      },
    },
    {
      Component: OrganizedSportsActivity,
      key: 'OrganizedSportsActivity',
      label: 'ORGANIZED SPORTS ACTIVITY INJURY RIDER',
      isApplied: getQuoteFieldValue<boolean>(organizedSportsActivityPaths.isOrganizedSportsActivityInjuryRiderApplied),
      disabled: true,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(organizedSportsActivityPaths.isOrganizedSportsActivityInjuryRiderApplied, isApplied);
      },
    },
    {
      Component: OutpatientPhysicianTreatements,
      key: 'OutpatientPhysicianTreatements',
      label: 'OUTPATIENT PHYSICIAN TREATMENTS',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isOutpatientPhysicianTreatmentsApplied),
      hidden: true,
    },
    // {
    //   Component: AutomobileAccidentRider,
    //   key: 'AutomobileAccidentRider',
    //   label: 'AUTOMOBILE ACCIDENT RIDER'
    // },
    // {
    //   Component: OrganizedSportsActivityInjuryRider,
    //   key: 'OrganizedSportsActivityInjuryRider',
    //   label: 'ORGANIZED SPORTS ACTIVITY INJURY RIDER',
    // },
    // {
    //   Component: GroupPolicyholderRider,
    //   key: 'GroupPolicyholderRider',
    //   label: 'GROUP POLICYHOLDER FACILITY RIDER',
    // },
  ];

  return (
    <FormWrapper title="Additional Options and Benefits" collapsible withSeparator>
      <QuoteFormConditionalSection sections={additionalOptNBenefits} />
    </FormWrapper>
  );
};
